//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import React, { useEffect, useMemo, useRef, useState } from "react";
import Hls from 'hls.js';
import styled from "styled-components";
import ReactPlayer from "react-player";

//  -- Components ---------------------
import { Col, Row, Tooltip , Button as AntdButton , Dropdown } from "antd";

//  -- Constants ----------------------
import COLOR from "utils/constants/color";
import {
  InteractionCategoryType,
  interactionType,
} from "utils/config/reference";
import {
  ClosedCaption,
  ExpandLessOutlined,
  ExpandMoreOutlined,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon
} from '@mui/icons-material';
import ClickOutside from "./OnClickOutside";
import { 
  Button, 
  Typography, 
  Slider,
  Popover 
} from '@mui/material'
import { v4 } from "uuid";
import { useCurrentUser } from "store/users/selectors";
import InteractionsAssignmentModal from "./AssignmentAiGenerate/InteractionsAssignmentModal";
import ToolSettingSvg from "../utils/assets/images/ToolSetting.svg";
import * as Sentry from '@sentry/react';
import { useAlert } from "./UI/alertSakeBar/alertProvider";
import { checkStandardExam, isYoutubeUrl } from "utils/methods/textMethod";
import YouTubePlayer from "components/ebook/YtPlayer";
import { useUserProfileConfig } from "store/users/selectors";
import usePermissionForUser from "./hooks/permissions/usePermissionForUser";
import TranscriptWithAudio from "./UI/transcriptWithAudio/transcriptWithAudio";
import { useExams } from "store/exams/selectors";
import { useStandardExams } from "store/standardexams";
import { useApExams } from "store/apexams/selectors";
import SentryWrap from 'components/Sentry';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
//  ----------------------------------------------------------------------------
//  Styles
//  ----------------------------------------------------------------------------
const Wrapper = styled.div`
  //   background-color: ${COLOR.green800};
  border-bottom-right-radius: 24px;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  padding: ${(p) => (p.page !== "balcon" ? "4px 12px 15px 13px" : "")};
  position: relative;
  width: 100%;

  &:last-child {
    margin-bottom: 0px;
  }
  @media only screen and (max-width: 1200px) {
    padding: ${(p) => p.page !== "balcon" && "4px 12px 15px 13px"};
  }
`;
const ToolButton =styled(Button)`
    min-width: unset !important;
`
const SubtitleToggleButton = styled(AntdButton)`
    position: absolute !important;
    right: 92px;
    bottom: 38px;
    background-color: transparent !important;
    color: white !important;
    z-index: 1;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;

  &:hover {
    background-color: rgba(0, 0, 0, 0.7) !important;
  }
`;

//  ----------------------------------------------------------------------------
//  Component
//  ----------------------------------------------------------------------------
//
//  Interaction
//
//  -- Description
//  TODO
//
//  -- Props
//  ...
//
//  -- State
//  ...
//
//  -- Methods
//  ...
//
//  -- Usage
//  ...
//

// Add this at the top level, outside of any component
const currentlyPlayingMedia = {
  current: null
};

const getTrack = async (url) => {
  const fetchVtt = async () => {
    try {
      const cacheBuster = `?v=${Date.now()}`; 
      const response = await fetch(`${url}${url.includes('?') ? '&' : '?'}${cacheBuster}`, {
        cache: "reload"
      });
      return await response.text();
    } catch (error) {
      Sentry.captureException(`230 Error fetching VTT file: ${error}`);
      console.error('Failed to load subtitles:', error);
      return '';
    }
  };
  return fetchVtt();
};
const TrackDownloader = ({url,style={}, pageId , chapter , from , enSrt, video, audioUrl , onClick }) => {
  const currentUser = useCurrentUser()
  const { isAiPermissionEnabled } = useUserProfileConfig();
  const hasSubtitles = url || enSrt;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { districtPermissionConfig , downloadAudio, downloadTranscipt} = usePermissionForUser();
  const {audio_transcript_download = false,  audio_download = false } = districtPermissionConfig;
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [assignmentAiModal, setassignmentAiModal] = useState(false);
  const generateQuestionRef = useRef()
  const showAlert = useAlert();
  const exams = useExams();
  const standard_exams = useStandardExams();
  const examsAp = useApExams();
  
  const isExam = useMemo(() => {
    const chapterId = `${chapter}`;
  
    const flatExams =
      exams?.flatMap(entry => entry.exams.map(exam => `${exam.bookId}`)) || [];
  
    const flatApExams =
      examsAp?.flatMap(entry => entry.exams.map(exam => `${exam.bookId}`)) || [];
      const isStandardExam = checkStandardExam(chapter,standard_exams)
  
    return (
      flatExams.includes(chapterId) ||
      flatApExams.includes(chapterId) ||
      isStandardExam
    );
  }, [exams, examsAp, standard_exams, chapter]);  

    const handleOpenTool = (event) => {
      setAnchorEl(event.currentTarget);
    };
    const handleCloseTool = () => {
      setAnchorEl(null);
    };
  const formatVttText = (vttText) => {
    if(!vttText) return ''
    return vttText
      .split('\n\n')
      .filter((block) => block.includes('-->'))
      .map((block) => {
        const [time, ...textLines] = block.split('\n');
        let k = textLines.join('\n').includes('-->') ? '\n' : `\n${time}\n`
        // remove all html tag from k 
        let sub_titile = textLines.join('\n').replace(/<[^>]*>/g, '');
        sub_titile = sub_titile.replace(/Generating images\.\.\./g, '').trim();
        return `${k}${sub_titile}\n`;
      })
      .join('\n');
  };

  const downloadVttAsPdf = async() => {
    handleCloseTool()
    const track_text = await getTrack(url)
    
    if(!track_text) return;
    const { default: jsPDF } = await import('jspdf');
    const fileName = `subtitles-${Date.now()}`;
    const doc = new jsPDF();
    const formattedText = formatVttText(track_text);
    const pageHeight = doc.internal.pageSize.height;
    const lineHeight = 10;
    const marginTop = 10;
    const maxLinesPerPage = Math.floor((pageHeight - marginTop) / lineHeight);

    const lines = formattedText.split('\n');
    let cursorY = marginTop;
    let lineCounter = 0;

    lines.forEach((line, index) => {
      if (lineCounter >= maxLinesPerPage) {
        doc.addPage();
        cursorY = marginTop;
        lineCounter = 0;
      }

      doc.text(line, 10, cursorY);
      cursorY += lineHeight;
      lineCounter++;
    });

    doc.save(`${fileName}${new Date().toDateString()}-es.pdf`);
  };
  const downloadVttAsPdfEnglish = async() => {
    handleCloseTool()
    const track_text = await getTrack(enSrt)
    if(!track_text) return;
    const { default: jsPDF } = await import('jspdf');
    const fileName = `subtitles-${Date.now()}`;
    const doc = new jsPDF();
    const formattedText = formatVttText(track_text);
    const pageHeight = doc.internal.pageSize.height;
    const lineHeight = 10;
    const marginTop = 10;
    const maxLinesPerPage = Math.floor((pageHeight - marginTop) / lineHeight);

    const lines = formattedText.split('\n');
    let cursorY = marginTop;
    let lineCounter = 0;

    lines.forEach((line, index) => {
      if (lineCounter >= maxLinesPerPage) {
        doc.addPage();
        cursorY = marginTop;
        lineCounter = 0;
      }

      doc.text(line, 10, cursorY);
      cursorY += lineHeight;
      lineCounter++;
    });

    doc.save(`${fileName}${new Date().toDateString()}-eng.pdf`);
  };

  const handleForDistrictAdmin = () =>{
    if (currentUser.role === 'admin') {
      showAlert('Please login as teacher!', "error");
      return true;
    }
    return false;
  }

  const onGenerateClick = (lang) =>{
    if (handleForDistrictAdmin()) return;
    if(!isAiPermissionEnabled){
      showAlert('Please enable AI permission to generate assignment.', 'error');
      return;
    }
    setassignmentAiModal(true);
    handleCloseTool();
    if (generateQuestionRef.current && generateQuestionRef.current.generateQuestions) {
      generateQuestionRef.current.generateQuestions(lang)
    }
  }

  const handleDownloadTranscript = async () => {
    setIsLoading(true);
    setIsDropdownOpen(false);
    await downloadTranscipt({ audioUrl });
    setIsLoading(false);
  }; 

  const handleDownloadAudio = async () => {
    setIsLoading(true);
    setIsDropdownOpen(false);
    await downloadAudio({ audioUrl });
    setIsLoading(false);
  };
  
  const Tools = useMemo(() => {
    // if (currentUser?.role == "student") return null;
    return (<>

      <div className="videoToolSet">
        {video && (
          <label>Video</label>
        )}
        {url && currentUser?.role !== "student" && (
         <ToolButton onClick={handleOpenTool} call_from={from}>
          <img src={ToolSettingSvg} alt="" title="" />
         </ToolButton>
        )}
      </div>

      <Popover 
        id={Boolean(anchorEl) ? 'simple-popover' : undefined} 
        open={Boolean(anchorEl)} 
        anchorEl={anchorEl} 
        onClose={handleCloseTool}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{vertical: 'top',horizontal: 'center' }}
        disableScrollLock={true}
      >
        <Typography sx={{ p: 2 }} className="chngOptList chngOptListCstm">
          <ul>
            {!isExam && <li><a onClick={()=>onGenerateClick('spanish')}>Generate Assignment with AI (es)</a></li>}
            {!isExam && enSrt ? <li><a onClick={()=>onGenerateClick('english')}>Generate Assignment with AI (en)</a></li> : null}
            {hasSubtitles ? (
            <>
            <li><a onClick={downloadVttAsPdf}>Download subtitles (es)</a></li>
            {enSrt ? <li><a onClick={downloadVttAsPdfEnglish}>Download subtitles (en)</a></li> : null}
            </>
            ) : (
            <>
              {audio_transcript_download && ( <li><a onClick={handleDownloadTranscript}> Download Transcript</a></li> )}
            </>
            )}
            {audio_download && ( <li><a onClick={handleDownloadAudio}>Download Audio</a></li> )}
          </ul>
        </Typography>
      </Popover>
    </>)
  }, [currentUser, anchorEl, generateQuestionRef.current])
  // if (!url) return null;
  return <>
  {Tools}
    <InteractionsAssignmentModal
      data={{}}
      assignmentAiModal={assignmentAiModal}
      setassignmentAiModal={setassignmentAiModal}
      content_type={"resources"}
      ref={generateQuestionRef}
      srt_url={url}
      page={pageId}
      chapter={chapter}

    />
  </>;
  // return (<div
  //   onClick={downloadVttAsPdf}
  //   style={{ backgroundColor: color.green100 , right:10 , ...style}}
  //   className="interaction-category-div flex gap-10px justify-between cursor-pointer"
  // >
  //   <CloudDownload color="#fff" style={{ fontSize: "22px" }} />
  // </div>)
}
const InteractionAudioCategory = ({ data , pageId , chapter }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [dropdownList, setDropDownList] = useState(
    data?.audio_metadata ? data?.audio_metadata : []
  );
  const audioPlayerRef = useRef(null);
  const interactionData = InteractionCategoryType?.find(
    (element) => element?.value === data?.category
  );
  const isElcancion = interactionData?.value === "elcancion";
  const [wrapperWidth, setWrapperWidth] = useState(0);
  const wrapperRef = useRef(null);

  const getSelectedTrackSubtitleUrls = () => {
    if (!selectedData || !data?.transcribes || !data?.srt_en_transcribe) {
      return {
        srt: data?.srt,
        srt_en: data?.srt_en
      };
    }
    const selectedTrackTranscribe = data?.transcribes?.find(
      transcribe => transcribe.id == selectedData.id
    );

    const selectedTrackTranscribeEng = data?.srt_en_transcribe?.find(
      transcribeEnglish => transcribeEnglish.id == selectedData.id
    );
    if (selectedTrackTranscribe) {
      return {
        srt: selectedTrackTranscribe.srt,
        srt_en: selectedTrackTranscribeEng?.srt_en || data?.srt_en
      };
    }
    return {
      srt: data?.srt,
      srt_en: data?.srt_en
    };
  };

  useEffect(() => {
    try {
      if (isElcancion && data?.default_generic_name?.length > 0) {
        const payload = {
          id: v4(),
          genreName: data?.default_generic_name
            ? data?.default_generic_name
            : "Untitled Name",
          url: data?.content,
        };

        setDropDownList(
          data?.audio_metadata ? [payload, ...data?.audio_metadata] : [payload]
        );

        setSelectedData(payload);
      }
    } catch (error) {
      Sentry.captureException(`231 Error in Interaction Audio Category: ${error}`);
      console.log(error);
    }
  }, [data]);

  const handleError = (error) => {
    console.error('Audio playback error:', error);
    // If we have access to the player's internal instance, try to reload
    if (audioPlayerRef.current?.getInternalPlayer()) {
      audioPlayerRef.current.getInternalPlayer().load();
    }
  };

  const toggleDropdown = () => {
    if (isElcancion) {
      setIsDropdownOpen((prev) => !prev);
    }
  };

  const handleAudioSelect = (e, url) => {
    e.stopPropagation();
    setSelectedData(url);
    setIsDropdownOpen(false);
  };
  const getNumericWidth = (element) => {
    if (!element) return 0;
    const computedWidth = window.getComputedStyle(element).width;
    return Math.floor(parseFloat(computedWidth)); // Convert to number and round down
  };

  useEffect(() => {
    if (wrapperRef.current) {
      let resizeObserver;
      try {
        resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            const newWidth = Math.floor(entry.contentRect.width);
            setWrapperWidth(newWidth);
          }
        });

        resizeObserver.observe(wrapperRef.current);

        setWrapperWidth(wrapperRef.current.offsetWidth);
      } catch (error) {
        Sentry.captureException(`232 Error in ResizeObserver: ${error}`);
        console.log(error);
      }

      return () => {
        if(resizeObserver){
          resizeObserver.disconnect();
        }
      };
    }
  }, []);

  // Add handlePlay function for audio
  const handlePlay = () => {
    if (currentlyPlayingMedia.current && currentlyPlayingMedia.current !== audioPlayerRef.current) {
      const player = currentlyPlayingMedia.current;
      if (player.getInternalPlayer) {
        const internalPlayer = player.getInternalPlayer();
        if (internalPlayer && typeof internalPlayer.pause === 'function') {
          internalPlayer.pause();
        }
      } else if (player.pause) {
        player.pause();
      }
    }
    currentlyPlayingMedia.current = audioPlayerRef.current;
  };
  
  const selectedTrackSubtitleUrls = getSelectedTrackSubtitleUrls();
  const { districtPermissionConfig } = usePermissionForUser();
  const {audio_download= false} = useMemo(() => {
    return districtPermissionConfig
  }, [districtPermissionConfig])
  return (
    <Wrapper ref={wrapperRef} className="1111 relative interaction-wrapper audioAdvCstm">

    <div className={`audioAdvContainUpr ${interactionData?.label ? '' : 'noAudiOption'}`}>
     <div className="audioAdvContain">
      <div className="audioAdvLftCstm">
       <div onClick={toggleDropdown}
        // style={{ backgroundColor: interactionData?.color }}
        className="audioAdvInrCstm flex gap-10px justify-between cursor-pointer prevent-select">

        {interactionData?.label ? 
         <span className="audioAdvLbl">{interactionData?.label}</span>
          :
          <span className="audioAdvLbl">Audio</span>
        }

        {isElcancion && dropdownList?.length > 0 && (
          <div className="audioAdvOptn">
            {isDropdownOpen ? (
              <ExpandLessOutlined />
            ) : (
              <ExpandMoreOutlined />
            )}
          </div>
        )}
      </div>

      {dropdownList?.length > 0 && selectedData?.genreName && (
        <div
          // style={{ backgroundColor: interactionData?.color }}
        >
          <Tooltip placement="top" title={selectedData?.genreName}>
            <span className="audioAdvSelectOptn">
              {selectedData?.genreName}
            </span>
          </Tooltip>
        </div>
      )}
      </div>

      {isElcancion && dropdownList?.length > 0 && isDropdownOpen && (
        <ClickOutside className="selectDropOptn" onClickOutside={() => setIsDropdownOpen(false)}>
          <InteractionDropdown className="selectDropIner">
            {dropdownList?.map((item, index) => (
              <div className={`${ item?.url === selectedData?.url ? "selectDropOptnActv" : "" }`}>
              <AudioMetaDataElement
                onClick={(e) => handleAudioSelect(e, item)}
                key={index}
                className="audio-metadata-element selectDropMeta">
                {item?.genreName?.length > 20 ? (
                  <Tooltip placement="top" title={item?.genreName}>
                    {item?.genreName.slice(0, 20) + "..."}
                  </Tooltip>
                ) : (
                  <>{item.genreName}</>
                )}
              </AudioMetaDataElement>
              </div>
            ))}
          </InteractionDropdown>
        </ClickOutside>
      )}
     </div>
      <div className="audioAdvContainTool">
       <TrackDownloader url={selectedTrackSubtitleUrls?.srt} enSrt={selectedTrackSubtitleUrls?.srt_en}  pageId={pageId} chapter={chapter} from={"multi_category"} video={false} audioUrl={selectedData?.url || data?.content} />
      </div>
     </div>

      <Row>
        <Col span={24} style={{ backgroundColor: '#ffffff' }}>
          <TranscriptWithAudio audioUrl={selectedData?.url || data.content} title={selectedData?.genreName || 'Interaction Audio'} isInteractionAudio={true}>
            <ReactPlayer
              ref={audioPlayerRef}
              url={selectedData?.url || data.content}
              height="40px"
              className="custom-player interactionCustomPlayer"
              width="100%"
              controls
              playsinline
              volume={0.4}
              onError={handleError}
              onPlay={handlePlay}
              config={{
                file: {
                  forceAudio: true,
                  attributes: { 
                    controlsList:  "nodownload",
                  },
                },
              }}
            />
          </TranscriptWithAudio>
        </Col>
      </Row>
    </Wrapper>
  );
};
const languages = [
  { code: "none", label: "None" },
  { code: "es", label: "ES" },
  { code: "en", label: "EN" }
];
const languages_es = [
  { code: "none", label: "None" },
  { code: "es", label: "ES" },
];

const useTrackVtt = (data) => {
  const [subtitleUrl, setSubtitleUrl] = useState('');
  const [subtitleUrlEng, setSubtitleUrlEng] = useState('');
  const [vttText, setVttText] = useState('');
  const [vttTextEng, setVttTextEng] = useState('');

  useEffect(() => {
    let url, urlEng;

    const fetchVtt = async (srtUrl) => {
      try {
        let text = await  getTrack(srtUrl);

        if (srtUrl.includes('.srt') || !text.includes('WEBVTT')) {
          text = 'WEBVTT\n\n' + text
            .replace(/\r/g, '')
            .replace(/(\d\d:\d\d:\d\d),(\d\d\d)/g, '$1.$2');
        }
        return text;
      } catch (error) {
        console.error('Failed to load subtitles:', error);
        Sentry.captureException(`233 Error fetching VTT file: ${error}`);
        return '';
      }
    };

    const loadSubtitles = async () => {
      if (data?.srt) {
        const vtt = await fetchVtt(data.srt);
        setVttText(vtt);
        if (vtt) {
          const blob = new Blob([vtt], { type: 'text/vtt' });
          url = URL.createObjectURL(blob);
          setSubtitleUrl(url);
        } else {
          setSubtitleUrl(null);
        }
      } else {
        setVttText('');
        setSubtitleUrl(null);
      }

      if (data?.srt_en) {
        const vttEng = await fetchVtt(data.srt_en);
        setVttTextEng(vttEng);
        if (vttEng) {
          const blob = new Blob([vttEng], { type: 'text/vtt' });
          urlEng = URL.createObjectURL(blob);
          setSubtitleUrlEng(urlEng);
        } else {
          setSubtitleUrlEng(null);
        }
      } else {
        setVttTextEng('');
        setSubtitleUrlEng(null);
      }
    };

    loadSubtitles();

    return () => {
      if (url) URL.revokeObjectURL(url);
      if (urlEng) URL.revokeObjectURL(urlEng);
    };
  }, [data]);

  return { subtitleUrl, vttText, vttTextEng, subtitleUrlEng };
};
const isIOS = () => {
  return (
    [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod'
    ].includes(navigator.platform) ||
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  );
};
const YtPlayer = ({ src, playerId }) => {
  const [isValidurl, setisvalidUrl] = useState(true)
  useEffect(() => {
    if (isYoutubeUrl(src)) {
      setisvalidUrl(true)
    } else {
      setisvalidUrl(false)
    }
  }, [src])
  if (!isValidurl) return null;
  return (<YouTubePlayer url={src} playerId={playerId} />)
}
const VideoPlayer = ({ data, pageId, chapter, index }) => {
  const [hover, setHover] = useState(false);
  const [selectedLang, setSelectedLang] = useState('es');
  const [selectedTrackUrl, setSelectedTrackUrl] = useState(data?.srt);
  const { subtitleUrl, subtitleUrlEng } = useTrackVtt(data)
  const [mute, setMute] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [lastVolume, setLastVolume] = useState(50);
  const [volume, setVolume] = useState(20);
  const videoRef = useRef(null);
  const [expanded, setExpanded] = useState(true);

  const handleAccordionChange = (event, isExpanded) => {
    setExpanded(isExpanded);
  
    // when accordion closes, pause video
    if (!isExpanded && videoRef.current) {
      videoRef.current.pause();
    }
  };
  const dataUrl = useMemo(() => data?.content, [data?.content]);
  const isHls = useMemo(() => typeof dataUrl === 'string' && dataUrl.includes('.m3u8'), [dataUrl]);
  const poster = useMemo(() => {
    if(data?.poster) return data?.poster;
    if(isHls){
      const videoId = dataUrl.split('/')[3];
      if(videoId){
      return `https://customer-quvpuvq5vklyms5a.cloudflarestream.com/${videoId}/thumbnails/thumbnail.jpg`
      } else {
        return null;
      }
    } else {
      return null;
    }
  }, [data?.poster,dataUrl,isHls]);


  // Update handlePlay for video
  const handlePlay = () => {
    if (currentlyPlayingMedia.current && currentlyPlayingMedia.current !== videoRef.current) {
      const player = currentlyPlayingMedia.current;
      if (player.getInternalPlayer) {
        const internalPlayer = player.getInternalPlayer();
        if (internalPlayer && typeof internalPlayer.pause === 'function') {
          internalPlayer.pause();
        }
      } else if (player.pause) {
        player.pause();
      }
    }
    currentlyPlayingMedia.current = videoRef.current;
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (videoRef.current) {
        if (document.hidden) {
          videoRef.current.pause();
        }
      }
    };

    const handlePageHide = () => {
      if (videoRef.current) {
        videoRef.current.pause();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handleError = (e) => {
        console.error('Video error:', e);
        video.load();
      };

      video.addEventListener('error', handleError);

      return () => video.removeEventListener('error', handleError);
    }
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isHls) return;

    let hls = null;

    const setupHls = () => {
      if (Hls.isSupported()) {
        hls = new Hls({
          startLevel: -1,
          maxBufferLength: 3,
          maxBufferSize: 6 * 1024 * 1024,
          lowLatencyMode: true,
          xhrSetup: (xhr) => {
            xhr.withCredentials = false;
          },
          backBufferLength: 0,
        });

        hls.loadSource(dataUrl);
        hls.attachMedia(video);

        hls.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.error('HLS Network Error:', {
                  type: data.type,
                  details: data.details,
                  url: data.url,
                  error: data.error?.message || data.error,
                  response: data.response,
                });
                hls.startLoad();
                break;

              case Hls.ErrorTypes.MEDIA_ERROR:
                console.error('HLS Media Error:', {
                  type: data.type,
                  details: data.details,
                  error: data.error?.message || data.error,
                });
                try {
                  hls.recoverMediaError();
                } catch (e) {
                  console.error('Failed to recover from media error:', e);
                  if (hls) {
                    hls.destroy();
                    hls = null;
                  }
                }
                break;

              default:
                console.error('HLS Fatal Error:', {
                  type: data.type,
                  details: data.details,
                  error: data.error?.message || data.error,
                });
                hls.destroy();
                break;
            }
          } else {
            console.warn('HLS Non-fatal Error:', {
              type: data.type,
              details: data.details,
            });
          }
        });

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
        });
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = dataUrl;
      }
    };

    setupHls();

    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, [dataUrl, isHls]);

  useEffect(() => {
    switch (selectedLang) {
      case 'es':
        setSelectedTrackUrl(subtitleUrl);
        break;
      case 'en':
        setSelectedTrackUrl(subtitleUrlEng);
        break;
      default:
        setSelectedTrackUrl(null);
    }
  }, [selectedLang, subtitleUrl, subtitleUrlEng]);

  useEffect(() => {
    const video = document.querySelector(`.ebook_video${index}`);
    if (video) {
      if (isIOS()) {
        video.muted = true; 
      } else {
        video.volume = 0.2;
      }
    }
  }, []);

  const handleVolumeChange = (val) => {
    if (val === 0) {
      setMute(true);
    } else {
      setMute(false);
    }
    setVolume(val);
    const video = document.querySelector(`.ebook_video${index}`);
    if (video) {
      video.volume = val / 100;
    }
  };

  const selected_lang = useMemo(() => languages.find(l => l.code === selectedLang)?.label, [selectedLang])
  const track_enabled = useMemo(() => selectedLang !== 'none' && selectedTrackUrl, [selectedLang, selectedTrackUrl]);
  const tracks = useMemo(() => subtitleUrlEng ? languages : languages_es, [subtitleUrl, subtitleUrlEng])
  
  return (
    <>
      <Accordion 
        expanded={expanded}
        onChange={handleAccordionChange} 
        className="videoToolAccordian"
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon style={{ color: '#fff' }} />}
          aria-controls="panel1-content"
          id="panel1-header"
          className="videoToolAccordianSummary"
        >
          <div className="videoToolSetWrapper"
            onClick={(e) => e.stopPropagation()}
            onFocus={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <TrackDownloader url={data?.srt} enSrt={data?.srt_en} style={{ top: "-29px" }} pageId={pageId} chapter={chapter} video={true} />
          </div>
        </AccordionSummary>
        <AccordionDetails className="videoToolAccordianDetails">
    <div className="relative videoWrapper"
      onMouseEnter={() => setHover(true)}
      onTouchStart={() => {
        setHover(true)
        setTimeout(() => {
          setHover(false);
        }, 3000)
      }}
      onMouseLeave={() => setHover(false)}
    >
      {(data?.srt || data?.srt_en) && hover && (
        <Dropdown
          menu={{
            items: tracks.map(({ code, label }) => ({
              key: code,
              label: label,
              onClick: () => {
                if (code === "none") {
                  setSelectedLang("none")
                } else {
                  setSelectedLang(code)
                }
              }
            }))
          }}
          trigger={["click"]}
        >
          <SubtitleToggleButton
            className="subtitle-toggle-button"
            onClick={e => e.preventDefault()}
          >
            {track_enabled
              ? <ClosedCaption color="#fff" />
              : <ClosedCaption style={{ color: "rgb(252 239 239 / 26%)" }} color="rgb(252 239 239 / 26%)" />
            } {selected_lang !== 'none' && selected_lang !== 'None' && selected_lang ? `(${selected_lang})` : ''}
          </SubtitleToggleButton>
        </Dropdown>
      )}
      <>
      <div className="videoMainUpr">
        <video
          className={`ebook_video${index}`}
          controls
          playsInline
          autoPlay={false}
          webkit-playsinline="true"
          preload="auto"
          poster={poster}
          controlsList="nodownload"
          crossOrigin="anonymous"
          style={{ height: '100%', width: '100%', borderRadius: '0px 0px 8px 8px' }}
          width="100%"
          ref={videoRef}
          onPlay={handlePlay}
          onMouseEnter={() => setShowVolumeSlider(true)}
          onTouchStart={() => {
            setShowVolumeSlider(true);
          }}
          onMouseLeave={() => setShowVolumeSlider(false)}
        >
          {!isHls && <source src={dataUrl} type="video/mp4" />}
          {selectedLang !== 'none' && selectedTrackUrl && (
            <track
              kind="subtitles"
              src={selectedTrackUrl}
              srcLang={selectedLang ? selectedLang : 'en'}
              default
            />
          )}
        </video>

        {!isIOS() && hover && (
        <div
          style={{ background: !showVolumeSlider && "none"}}
          onMouseEnter={() => setShowVolumeSlider(true)}
          onTouchStart={() => {
            setShowVolumeSlider(true);
          }}
          onMouseLeave={() => setShowVolumeSlider(false)}
          className="ebookVolumeSlider 1211212122"
        >
          {mute ? (
            <VolumeOffIcon
              style={{ color: "white", rotate: "90deg" }}
              onClick={() => {
                const newVolume = lastVolume;
                const video = document.querySelector(`.ebook_video${index}`);
                if (video) {
                  video.volume = newVolume / 100;
                }
                setVolume(newVolume);
                setMute((prev) => !prev);
              }}
            />
          ) : (
            <VolumeUpIcon
              style={{ color: "white", rotate: "90deg" }}
              onClick={() => {
                setLastVolume(volume);
                const newVolume = 0;
                const video = document.querySelector(`.ebook_video${index}`);
                if (video) {
                  video.volume = newVolume / 100;
                }
                setVolume(newVolume);
                setMute((prev) => !prev);
              }}
            />
          )}
          {showVolumeSlider && (
            <Slider
              orientation="vertical"
              defaultValue={50}
              value={volume}
              onChange={(e, val) => handleVolumeChange(val)}
              aria-labelledby="continuous-slider"
              min={0}
              max={100}
              step={1}
              valueLabelDisplay="auto"
              sx={{
                height: '120px',
                // some simple styling
                "& .MuiSlider-thumb": { width: 16, height: 16 },
                "& .MuiSlider-track": { width: 4 },
                "& .MuiSlider-rail": { width: 4, opacity: 0.3 },
              }}
              style={{ color: "#1976d2", width: "85px", marginLeft: "12px", rotate: "90deg" }}
            />
          )}
        </div>
        )}
      </div>
      </>
    </div>
    </AccordionDetails>

      </Accordion>
   </>
 )
}
const Interaction = ({ data, page ,  pageId='' , chapter='' , index=0 }) => {
  const audioPlayerRef = useRef(null);

  const handleError = (error) => {
    console.error('Audio playback error:', error);
    if (audioPlayerRef.current?.getInternalPlayer()) {
      audioPlayerRef.current.getInternalPlayer().load();
    }
  };

  const handlePlay = () => {
    if (currentlyPlayingMedia.current && currentlyPlayingMedia.current !== audioPlayerRef.current) {
      const player = currentlyPlayingMedia.current;
      if (player.getInternalPlayer) {
        const internalPlayer = player.getInternalPlayer();
        if (internalPlayer && typeof internalPlayer.pause === 'function') {
          internalPlayer.pause();
        }
      } else if (player.pause) {
        player.pause();
      }
    }
    currentlyPlayingMedia.current = audioPlayerRef.current;
  };

  return (
    <SentryWrap>
    <Wrapper pagae={page} className={`relative ${interactionType[data?.category]||data?.srt ? 'interaction-wrapper' : ''}`} style={{marginTop:data?.srt?"1px":""}}>
      <Row>
        <Col span={24}>
          {data.typeId === "audio" && (
            <>
              {/* {interactionType[data?.category] ? ( */}
                <InteractionAudioCategory data={data} pageId={pageId} chapter={chapter} />
              {/* // ) : ( */}
              {/* <div className="relative">
                <TrackDownloader url={data?.srt} enSrt={data?.srt_en} style={{ top:"-29px"}}  pageId={pageId} chapter={chapter} />
                <ReactPlayer
                  ref={audioPlayerRef}
                  url={data.content}
                  height="40px"
                  className="custom-player 111"
                  width="100%"
                  controls
                  volume={0.4}
                  playsinline
                  onError={handleError}
                  onPlay={handlePlay}
                  config={{
                    file: {
                      attributes: { 
                        controlsList: "nodownload",
                      }
                    },
                  }}
                />
              </div> */}
              {/* )} */}
            </>
          )}
          {data.typeId === "video" && (<VideoPlayer data={data} pageId={pageId} chapter={chapter} index={index} />)}
          {data.typeId === "youtube" && (<YtPlayer src={data.content} playerId={data.id} />)}
        </Col>
      </Row>
    </Wrapper>
    </SentryWrap>
  );
};



export default Interaction;

const AudioMetaDataElement = styled.div`
  outline: none;
`;

const InteractionDropdown = styled.div`
  outline: none;
`;
